/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   DataPipelineMonitoringPage.tsx
 *     Feature         :   191-pipeline-monitoring-platform (supersedes 148-dw-job-monitoring)
 *     Description     :   Data Pipeline Monitoring — registry-driven page: domain-grouped
 *                         health overview → unified runs explorer → detail drawer. The
 *                         pipeline_monitoring_v2_enabled flag remains as a kill switch
 *                         (the legacy hard-coded ODS/DW tabs were retired at cutover).
 *     Constitution    :   v1.18.0 | Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-05-21 (rebuilt 2026-06-11)
 *     Modified        :   2026-06-14 | Conformed to Spec 197 design blueprint — page shell
 *                         migrated onto PageLayout; loading/error onto LoadingState/ErrorState;
 *                         kill-switch notice onto a token-styled Card; raw grays → tokens.
 * =========================================================================================
 */

import React, { useState } from 'react';
import { Activity } from 'lucide-react';
import { PipelineHealthOverview } from '../../components/operations/PipelineHealthOverview';
import { UnifiedRunsExplorer } from '../../components/operations/UnifiedRunsExplorer';
import { RunDetailDrawer } from '../../components/operations/RunDetailDrawer';
import { PageLayout, Card, LoadingState, ErrorState } from '../../components/ui';
import {
  usePipelineMonitoringV2Flag,
  usePipelineOverview,
} from '../../hooks/useMonitoredPipelines';

const PAGE_TITLE = 'Data Pipeline Monitoring';
const PAGE_DESCRIPTION = 'Registry-driven monitoring across all data-movement processes';
const PAGE_ICON = <Activity className="h-6 w-6" />;

function RegistryDrivenPage() {
  const { data, isLoading, error } = usePipelineOverview();
  const [selectedRun, setSelectedRun] = useState<{ pipelineKey: string; batchId: string } | null>(null);

  const domains = data?.domains ?? [];
  const flatRegistry = domains.flatMap((d) => d.pipelines);
  const selectedPipeline = selectedRun
    ? flatRegistry.find((p) => p.pipelineKey === selectedRun.pipelineKey)
    : undefined;

  return (
    <PageLayout
      helpId="auto.data-pipeline-monitoring"
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      icon={PAGE_ICON}
      maxWidth="wide"
    >
      <div data-testid="pipeline-monitoring-v2">
        {error ? (
          <ErrorState
            title="Failed to load monitored pipelines"
            message="Could not load the pipeline monitoring registry."
          />
        ) : isLoading ? (
          <LoadingState rows={6} />
        ) : (
          <div className="space-y-6">
            <PipelineHealthOverview domains={domains} />

            <div>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                Runs
              </h2>
              <UnifiedRunsExplorer
                pipelines={flatRegistry}
                selectedBatchId={selectedRun?.batchId ?? null}
                onRunSelect={(pipelineKey, batchId) => setSelectedRun({ pipelineKey, batchId })}
              />
            </div>

            <RunDetailDrawer
              pipelineKey={selectedRun?.pipelineKey ?? null}
              batchId={selectedRun?.batchId ?? null}
              pipelineDisplayName={selectedPipeline?.displayName}
              onClose={() => setSelectedRun(null)}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
}

export default function DataPipelineMonitoringPage() {
  const { data: v2Enabled, isLoading } = usePipelineMonitoringV2Flag();

  if (isLoading) {
    return (
      <PageLayout title={PAGE_TITLE} description={PAGE_DESCRIPTION} icon={PAGE_ICON} maxWidth="wide">
        <LoadingState rows={6} />
      </PageLayout>
    );
  }

  // Kill switch: pipeline_monitoring_v2_enabled (global_settings) — defaults true since
  // cutover; flipping it off disables the page rather than reviving the retired tabs.
  if (v2Enabled === false) {
    return (
      <PageLayout title={PAGE_TITLE} description={PAGE_DESCRIPTION} icon={PAGE_ICON} maxWidth="wide">
        <Card className="border-[var(--color-status-warning)]/40 bg-[var(--color-status-warning)]/5 p-4 text-sm text-[var(--color-text-primary)]">
          Pipeline monitoring is currently disabled by an administrator
          (<span className="font-mono text-[var(--color-text-secondary)]">pipeline_monitoring_v2_enabled</span>).
        </Card>
      </PageLayout>
    );
  }

  return <RegistryDrivenPage />;
}
